import { useCallback, useEffect, useRef } from 'react';
import { useCall } from '../context/CallContext';
import {
  Phone, PhoneOff, Video, VideoOff, Mic, MicOff, Volume2
} from 'lucide-react';

const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

// Play helper with retry — handles iOS Safari autoplay rejection cleanly
async function safePlay(el, onBlocked, onReady) {
  if (!el) return;
  try {
    await el.play();
    onReady?.();
  } catch (err) {
    // NotAllowedError → browser blocked autoplay; show tap-to-play button
    if (err?.name === 'NotAllowedError') {
      onBlocked?.();
    } else {
      console.warn('media play() failed:', err?.name || err);
    }
  }
}

export default function CallOverlay() {
  const {
    callState, callType, peerName, isIncoming,
    elapsed, localStream, remoteStream,
    muted, videoOff, audioBlocked,
    acceptCall, rejectCall, endCall,
    toggleMute, toggleVideo,
    markAudioBlocked, markAudioReady,
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // ── Callback refs guarantee srcObject is (re)attached whenever the element mounts
  // (critical for state transitions: outgoing-overlay → active-overlay remounts the audio tag).
  const setRemoteAudioEl = useCallback((el) => {
    remoteAudioRef.current = el;
    if (el && remoteStream) {
      if (el.srcObject !== remoteStream) el.srcObject = remoteStream;
      el.volume = 1.0;
      safePlay(el, markAudioBlocked, markAudioReady);
    }
  }, [remoteStream, markAudioBlocked, markAudioReady]);

  const setRemoteVideoEl = useCallback((el) => {
    remoteVideoRef.current = el;
    if (el && remoteStream) {
      if (el.srcObject !== remoteStream) el.srcObject = remoteStream;
      safePlay(el, markAudioBlocked, markAudioReady);
    }
  }, [remoteStream, markAudioBlocked, markAudioReady]);

  const setLocalVideoEl = useCallback((el) => {
    localVideoRef.current = el;
    if (el && localStream) {
      if (el.srcObject !== localStream) el.srcObject = localStream;
      el.muted = true; // never echo ourselves
      safePlay(el);
    }
  }, [localStream]);

  // Keep srcObject in sync when the stream changes but the element has not remounted
  useEffect(() => {
    const a = remoteAudioRef.current;
    if (a && remoteStream && a.srcObject !== remoteStream) {
      a.srcObject = remoteStream;
      a.volume = 1.0;
      safePlay(a, markAudioBlocked, markAudioReady);
    }
    const v = remoteVideoRef.current;
    if (v && remoteStream && v.srcObject !== remoteStream) {
      v.srcObject = remoteStream;
      safePlay(v, markAudioBlocked, markAudioReady);
    }
  }, [remoteStream, markAudioBlocked, markAudioReady]);

  // When the user taps the tap-to-play button, we retry both elements
  const resumeAudio = () => {
    safePlay(remoteAudioRef.current, markAudioBlocked, markAudioReady);
    safePlay(remoteVideoRef.current, markAudioBlocked, markAudioReady);
  };

  // iOS Safari requires audio/video .play() to originate from a direct user gesture.
  // Priming the media elements inside the Accept button click unlocks autoplay before
  // the remote MediaStream arrives, so the first remote audio packets are heard immediately.
  const handleAccept = useCallback(async () => {
    try {
      // Fire synchronous .play() on any existing media elements to unlock autoplay policy.
      // They have no srcObject yet → calls resolve quickly; iOS still registers the gesture.
      remoteAudioRef.current?.play?.().catch(() => {});
      remoteVideoRef.current?.play?.().catch(() => {});
    } catch (_) { /* ignore */ }
    await acceptCall();
  }, [acceptCall]);

  if (callState === 'idle') return null;

  const isVideo = callType === 'video';
  const initial = (peerName || 'U')[0].toUpperCase();

  // ── Persistent remote-audio element shared across every overlay state ──
  // Kept OUTSIDE the conditional overlays so transitions (calling → active) never remount it.
  const persistentAudio = (
    <audio
      ref={setRemoteAudioEl}
      autoPlay
      playsInline
      data-testid="remote-audio"
      style={{ position: 'fixed', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
    />
  );

  // ── Incoming call (ringing) ──
  if (callState === 'ringing' && isIncoming) {
    return (
      <>
        {persistentAudio}
        <div className="fixed inset-0 z-[9999] bg-obsidian/95 backdrop-blur-xl flex items-center justify-center" data-testid="incoming-call-overlay">
          <div className="text-center animate-in">
            <div className="relative mx-auto mb-8">
              <div className="w-28 h-28 rounded-full bg-white/10 flex items-center justify-center mx-auto animate-pulse">
                <span className="text-white text-4xl font-serif">{initial}</span>
              </div>
              <div className="absolute inset-0 w-28 h-28 rounded-full border-2 border-white/20 animate-ping mx-auto" />
            </div>

            <p className="text-white/60 text-xs uppercase tracking-[0.3em] mb-2">Incoming {isVideo ? 'Video' : 'Audio'} Call</p>
            <h2 className="text-white text-2xl font-serif mb-12">{peerName || 'Unknown'}</h2>

            <div className="flex items-center justify-center gap-10">
              <button
                onClick={rejectCall}
                className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors shadow-2xl"
                data-testid="decline-call-btn"
              >
                <PhoneOff size={24} />
              </button>
              <button
                onClick={handleAccept}
                className="w-16 h-16 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center transition-colors shadow-2xl animate-bounce"
                data-testid="accept-call-btn"
              >
                {isVideo ? <Video size={24} /> : <Phone size={24} />}
              </button>
            </div>
            <div className="flex justify-center gap-10 mt-3">
              <span className="text-red-400 text-[10px] uppercase tracking-wider">Decline</span>
              <span className="text-emerald-400 text-[10px] uppercase tracking-wider">Accept</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Outgoing call (calling/waiting) ──
  if (callState === 'calling') {
    return (
      <>
        {persistentAudio}
        <div className="fixed inset-0 z-[9999] bg-obsidian/95 backdrop-blur-xl flex items-center justify-center" data-testid="outgoing-call-overlay">
          <div className="text-center animate-in">
            <div className="w-28 h-28 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-8">
              <span className="text-white text-4xl font-serif">{initial}</span>
            </div>
            <p className="text-white/60 text-xs uppercase tracking-[0.3em] mb-2">Calling...</p>
            <h2 className="text-white text-2xl font-serif mb-2">{peerName || 'Unknown'}</h2>
            <p className="text-white/40 text-xs mb-12 animate-pulse">Waiting for answer</p>

            <button
              onClick={endCall}
              className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors shadow-2xl mx-auto"
              data-testid="cancel-call-btn"
            >
              <PhoneOff size={24} />
            </button>
            <p className="text-red-400 text-[10px] uppercase tracking-wider mt-3">Cancel</p>
          </div>
        </div>
      </>
    );
  }

  // ── Active call ──
  if (callState === 'active') {
    return (
      <>
        {persistentAudio}
        <div className="fixed inset-0 z-[9999] bg-obsidian flex flex-col" data-testid="active-call-overlay">
          {/* Video area */}
          <div className="flex-1 relative">
            {isVideo ? (
              remoteStream ? (
                <video
                  ref={setRemoteVideoEl}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="remote-video"
                />
              ) : (
                // Waiting on remote video track
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <span className="text-white text-3xl font-serif">{initial}</span>
                    </div>
                    <h2 className="text-white text-xl font-serif">{peerName || 'Unknown'}</h2>
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mt-3">Connecting video…</p>
                  </div>
                </div>
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-3xl font-serif">{initial}</span>
                  </div>
                  <h2 className="text-white text-xl font-serif">{peerName || 'Unknown'}</h2>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mt-3">Audio call</p>
                </div>
              </div>
            )}

            {/* Self preview (PiP) */}
            {isVideo && localStream && (
              <div className="absolute top-4 right-4 w-28 sm:w-36 aspect-[3/4] rounded-2xl overflow-hidden border-2 border-white/30 shadow-2xl bg-obsidian">
                {videoOff ? (
                  <div className="w-full h-full flex items-center justify-center bg-obsidian">
                    <VideoOff size={20} className="text-white/40" />
                  </div>
                ) : (
                  <video ref={setLocalVideoEl} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="local-video-pip" />
                )}
              </div>
            )}

            {/* Timer & status */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <div className="bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs font-mono" data-testid="call-timer">
                {formatTime(elapsed)}
              </div>
              <div className="bg-emerald-600/80 px-2 py-1 rounded-full text-white text-[9px] font-medium uppercase tracking-wider flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Connected
              </div>
            </div>

            {/* Autoplay-blocked banner (iOS Safari) */}
            {audioBlocked && (
              <button
                onClick={resumeAudio}
                data-testid="tap-to-hear-btn"
                className="absolute bottom-28 left-1/2 -translate-x-1/2 bg-amber-500 hover:bg-amber-400 text-obsidian font-semibold px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 text-sm animate-pulse"
              >
                <Volume2 size={16} /> Tap to hear audio
              </button>
            )}
          </div>

          {/* Controls bar */}
          <div className="bg-obsidian/90 backdrop-blur-xl border-t border-white/10 py-5 px-6 safe-area-bottom" data-testid="call-controls">
            <div className="flex items-center justify-center gap-5 max-w-sm mx-auto">
              <button
                onClick={toggleMute}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${muted ? 'bg-red-600 text-white' : 'bg-white/15 text-white hover:bg-white/25'}`}
                data-testid="call-toggle-mute"
              >
                {muted ? <MicOff size={20} /> : <Mic size={20} />}
              </button>

              {isVideo && (
                <button
                  onClick={toggleVideo}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${videoOff ? 'bg-red-600 text-white' : 'bg-white/15 text-white hover:bg-white/25'}`}
                  data-testid="call-toggle-video"
                >
                  {videoOff ? <VideoOff size={20} /> : <Video size={20} />}
                </button>
              )}

              <button
                onClick={endCall}
                className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors shadow-xl"
                data-testid="call-end-btn"
              >
                <PhoneOff size={22} />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
}
